import { sectionRenderer } from '@theme/section-renderer';

/** Explicit, independent pagination for source homepage rows. */
class AdasateeHomePagination extends HTMLElement {
  generation = 0;
  pending = false;
  retry = false;
  request;
  onClick = (event) => {
    if (!event.target.closest('[data-home-load-more]')) return;
    event.preventDefault();
    void this.loadNext();
  };

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.setAttribute('aria-busy', 'false');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.generation++;
    this.request?.abort();
    this.pending = false;
    this.setLoading(false);
  }

  resolveNextURL(raw, base) {
    if (!raw?.trim()) throw new Error('Missing next URL');
    const url = new URL(raw, base);
    // Shopify can emit homepage-root links even on the localized homepage.
    if (url.pathname === '/') url.pathname = base.pathname;
    if (url.origin !== base.origin || url.pathname !== base.pathname || url.username || url.password) {
      throw new Error('Invalid homepage route');
    }
    for (const key of ['preview_theme_id', 'preview_key']) {
      url.searchParams.delete(key);
      if (base.searchParams.has(key)) url.searchParams.set(key, base.searchParams.get(key));
    }
    url.hash = '';
    return url;
  }

  validateBatch(html, expected) {
    if (typeof html !== 'string' || !html.trim()) throw new Error('Missing section HTML');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const section = doc.getElementById(`shopify-section-${expected.sectionId}`);
    const rows = section?.querySelectorAll('adasatee-home-pagination');
    if (rows?.length !== 1) throw new Error('Missing or ambiguous row');
    const row = rows[0];
    const integer = (raw) => /^\d+$/.test(raw ?? '') ? Number(raw) : NaN;
    const page = integer(row.dataset.page);
    const pages = integer(row.dataset.pages);
    const total = integer(row.dataset.total);
    if (row.dataset.sectionId !== expected.sectionId || row.dataset.poolId !== expected.poolId ||
        page !== expected.page || total !== expected.total || pages !== Math.ceil(total / 8) || page < 1 || page > pages) {
      throw new Error('Unexpected batch metadata');
    }
    const grids = row.querySelectorAll('[data-home-grid]');
    if (grids.length !== 1) throw new Error('Missing or ambiguous grid');
    const brand = this.querySelector('[data-home-grid]')?.classList.contains('adasatee-brand-products__grid');
    if (grids[0].classList.contains('adasatee-brand-products__grid') !== brand) throw new Error('Unexpected card presentation');
    const cards = Array.from(grids[0].children);
    const ids = new Set(expected.loadedIds);
    if (cards.length !== Math.min(8, total - (page - 1) * 8)) throw new Error('Incomplete batch');
    for (const card of cards) {
      const id = card.getAttribute('data-home-product-id');
      if (!id?.trim() || ids.has(id) || card.querySelector('[data-home-product-id]')) throw new Error('Invalid product identity');
      let link;
      if (brand) {
        const title = card.querySelector(':scope > a > .adasatee-brand-products__title');
        const price = card.querySelector(':scope > .adasatee-brand-products__price');
        if (!card.matches('article.adasatee-brand-products__card') || !title?.textContent.trim() || !price?.textContent.trim()) {
          throw new Error('Invalid brand card');
        }
        link = title.parentElement;
      } else {
        const products = card.querySelectorAll('product-card');
        const product = products[0];
        const content = product?.querySelector(':scope > .product-card__content');
        if (!card.matches('.resource-list__item') || products.length !== 1 || product.parentElement !== card ||
            !product.matches('.product-card') || product.dataset.productId !== id || !content?.textContent.trim()) {
          throw new Error('Invalid rich card');
        }
        link = product.querySelector(':scope > a.product-card__link');
      }
      const destination = link?.getAttribute('href');
      if (!destination?.trim()) throw new Error('Missing product destination');
      const productURL = new URL(destination, window.location.href);
      if (productURL.origin !== window.location.origin || productURL.username || productURL.password ||
          !/\/products\/[^/]+\/?$/.test(productURL.pathname)) throw new Error('Invalid product destination');
      ids.add(id);
    }
    const nextURL = row.dataset.nextUrl ?? '';
    const control = row.querySelector('[data-home-load-more]');
    if (page < pages) {
      if (!nextURL || control?.getAttribute('href') !== nextURL) throw new Error('Missing next control');
      const next = this.resolveNextURL(nextURL, new URL(window.location.href));
      const current = this.resolveNextURL(this.dataset.nextUrl, new URL(window.location.href));
      // Shopify may name each paginate parameter differently. Discover it from the
      // server URL instead of assuming that the parameter is named "page".
      const pageKeys = [...current.searchParams].filter(([key, value]) => !['preview_theme_id', 'preview_key', 'section_id'].includes(key) && value === String(page));
      if (pageKeys.length !== 1 || next.searchParams.getAll(pageKeys[0][0]).length !== 1 ||
          next.searchParams.get(pageKeys[0][0]) !== String(page + 1)) throw new Error('Ambiguous or non-forward next page');
    } else if (nextURL || control) {
      throw new Error('Unexpected terminal next page');
    }
    return { cards, nextURL, page, pages, total };
  }

  setLoading(loading) {
    this.setAttribute('aria-busy', String(loading));
    const control = this.querySelector('[data-home-load-more]');
    if (!control) return;
    if (!this.idleLabel) this.idleLabel = control.textContent;
    control.setAttribute('aria-disabled', String(loading));
    if ('disabled' in control) control.disabled = loading;
    control.textContent = loading ? control.dataset.loadingLabel : this.retry ? control.dataset.retryLabel : this.idleLabel;
  }

  async loadNext() {
    if (this.pending || !this.isConnected || !this.dataset.nextUrl) return;
    const grid = this.querySelector('[data-home-grid]');
    const control = this.querySelector('[data-home-load-more]');
    const status = this.querySelector('[data-home-status]');
    if (!grid || !control || !status) return;
    const generation = this.generation;
    const request = new AbortController();
    this.request = request;
    this.pending = true;
    this.setLoading(true);
    status.textContent = control.dataset.loadingLabel;
    try {
      const url = this.resolveNextURL(this.dataset.nextUrl, new URL(window.location.href));
      const html = await sectionRenderer.getSectionHTML(this.dataset.sectionId, !this.retry, url, request.signal);
      if (request.signal.aborted || generation !== this.generation || !this.isConnected) return;
      const batch = this.validateBatch(html, {
        sectionId: this.dataset.sectionId,
        poolId: this.dataset.poolId,
        page: Number(this.dataset.page) + 1,
        total: Number(this.dataset.total),
        loadedIds: new Set(Array.from(grid.children, card => card.getAttribute('data-home-product-id'))),
      });
      // Moving the original card nodes keeps native links and lets the theme's
      // custom elements run their existing connectedCallback lifecycle on insert.
      grid.append(...batch.cards);
      this.dataset.page = String(batch.page);
      this.dataset.pages = String(batch.pages);
      this.dataset.nextUrl = batch.nextURL;
      this.retry = false;
      status.textContent = '';
      if (batch.nextURL) {
        control.setAttribute('href', batch.nextURL);
      } else {
        const heldFocus = control.contains(document.activeElement);
        control.remove();
        status.textContent = status.dataset.completeLabel;
        if (heldFocus) { status.tabIndex = -1; status.focus(); }
      }
    } catch (error) {
      if (request.signal.aborted || generation !== this.generation || !this.isConnected) return;
      this.retry = true;
      status.textContent = status.dataset.errorLabel;
    } finally {
      if (generation === this.generation && this.request === request) {
        this.pending = false;
        this.request = undefined;
        this.setLoading(false);
      }
    }
  }
}

if (!customElements.get('adasatee-home-pagination')) {
  customElements.define('adasatee-home-pagination', AdasateeHomePagination);
}
